import { type MinimumFlag, minimumFlags, SECTION_RULES } from './classify';
import {
  bandForScore,
  COPY,
  formatPercentile,
  formatPercentileOrdinal,
  isValidScore,
  SCHOOLS,
  sectionPercentile,
} from './data';
import { interpolate } from './interpolate';
import {
  type Band,
  type CtaCopy,
  type PartialSectionScores,
  type School,
  SECTION_KEYS,
  type SectionKey,
  type SectionScores,
} from './types';

export const SECTION_THRESHOLDS = {
  balancedMaxSpread: 2,
  belowCutoffMax: 122,
  mostlyBalancedMaxSpread: 4,
  riskMax: 124,
  strengthMin: 127,
  strengthPercentileMin: 50,
  watchMax: 126,
} as const;

export type SectionLevel = 'below_cutoff' | 'neutral' | 'risk' | 'strength' | 'watch';
export type BalanceLevel = 'balanced' | 'lopsided' | 'mostly_balanced';
export type RetakeVerdict = 'consider_section' | 'no' | 'no_but_watch' | 'yes_section' | 'yes_total';
export type SectionLeadRoute = 'fit_check' | 'prep';
export type SectionLeadVariant = 'balanced' | 'prep' | 'uneven';

export function sectionLevel(value: number, percentile: number | null): SectionLevel {
  if (value <= SECTION_THRESHOLDS.belowCutoffMax) return 'below_cutoff';
  if (value <= SECTION_THRESHOLDS.riskMax) return 'risk';
  if (value <= SECTION_THRESHOLDS.watchMax) return 'watch';
  if (percentile !== null && percentile >= SECTION_THRESHOLDS.strengthPercentileMin) return 'strength';
  return 'neutral';
}

export function balanceForSpread(spread: number): BalanceLevel {
  if (spread <= SECTION_THRESHOLDS.balancedMaxSpread) return 'balanced';
  return spread <= SECTION_THRESHOLDS.mostlyBalancedMaxSpread ? 'mostly_balanced' : 'lopsided';
}

export function retakeVerdict(bandId: number | null, levels: readonly SectionLevel[]): RetakeVerdict {
  if (bandId !== null && bandId <= 2) return 'yes_total';
  if (levels.includes('below_cutoff')) return bandId !== null && bandId <= 4 ? 'yes_section' : 'consider_section';
  if (levels.includes('risk')) return 'consider_section';
  if (levels.includes('watch')) return 'no_but_watch';
  return 'no';
}

export type SectionAssessment = Readonly<{
  key: SectionKey;
  level: SectionLevel;
  message: string;
  name: string;
  percentile: string;
  percentileOrdinal: string;
  short: string;
  value: number;
}>;

function assess(key: SectionKey, value: number): SectionAssessment {
  const copy = COPY.section_feedback.sections[key];
  const percentile = sectionPercentile(key, value);
  const level = sectionLevel(value, percentile);
  const values = {
    percentile: formatPercentile(percentile),
    percentile_ordinal: formatPercentileOrdinal(percentile),
    section_short: copy.short,
    value,
  };

  return {
    key,
    level,
    message: interpolate(level === 'neutral' ? COPY.section_feedback.neutral : copy[level], values),
    name: copy.name,
    percentile: values.percentile,
    percentileOrdinal: values.percentile_ordinal,
    short: copy.short,
    value,
  };
}

export function sectionAssessments(sections: PartialSectionScores): readonly SectionAssessment[] {
  const assessments: SectionAssessment[] = [];
  for (const key of SECTION_KEYS) {
    const value = sections[key];
    if (value !== undefined) assessments.push(assess(key, value));
  }
  return assessments;
}

// Weakest section first, so the feedback opens with the section that needs attention.
export function lowestFirst(assessments: readonly SectionAssessment[]): readonly SectionAssessment[] {
  return [...assessments].sort((a, b) => a.value - b.value);
}

export function completeSections(sections: PartialSectionScores): SectionScores | null {
  const { bbf, cars, cpf, psf } = sections;
  if (bbf === undefined || cars === undefined || cpf === undefined || psf === undefined) return null;
  return { bbf, cars, cpf, psf };
}

export type CutoffSchool = Readonly<{
  flags: readonly MinimumFlag[];
  name: string;
  source_url: string | null;
}>;

export function cutoffSchools(sections: SectionScores, schools: readonly School[] = SCHOOLS): readonly CutoffSchool[] {
  const missed: CutoffSchool[] = [];
  for (const school of schools) {
    const flags = minimumFlags({ sections }, school);
    if (flags.length > 0)
      missed.push({ flags, name: school.name, source_url: school.requirements_source_url ?? school.source_url });
  }
  return missed;
}

export type CarsCanadaSummary = Readonly<{ cleared: number; message: string; total: number }>;

// Canadian CARS thresholds: a school's structured cars_minimum, or else each published rule with a CARS minimum
// in sectionRequirements.json, so Alberta's two applicant groups count as two thresholds.
export function carsCanadaSummary(cars: number, schools: readonly School[] = SCHOOLS): CarsCanadaSummary | null {
  const minimums = schools
    .filter(school => school.country === 'CA')
    .flatMap(school =>
      school.cars_minimum !== null ?
        [school.cars_minimum]
      : SECTION_RULES.filter(rule => rule.school_key === school.key).map(rule => rule.minimums.cars),
    )
    .filter(minimum => minimum !== null);
  if (minimums.length === 0) return null;
  const cleared = minimums.filter(minimum => cars >= minimum).length;
  return {
    cleared,
    message: interpolate(COPY.section_feedback.cars_canada, { cleared, total: minimums.length, value: cars }),
    total: minimums.length,
  };
}

export type SectionSumError = Readonly<{ linkScore: number | null; message: string; sum: number }>;

export type SectionLead = CtaCopy & Readonly<{ route: SectionLeadRoute; variant: SectionLeadVariant }>;

// "CARS", "CARS and Psych/Soc", "Chem/Phys, CARS and Psych/Soc".
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// Every section tied for the lowest score, in report order. A tie is named in full, never as one section.
export function lowestSections(assessments: readonly SectionAssessment[]): readonly SectionAssessment[] {
  const min = Math.min(...assessments.map(assessment => assessment.value));
  return assessments.filter(assessment => assessment.value === min);
}

// The call to action under the feedback. Its headline has to agree with the verdict above it.
export function sectionLead(
  balance: BalanceLevel,
  levels: readonly SectionLevel[],
  lowest: readonly SectionAssessment[],
): SectionLead {
  const names = joinNames(lowest.map(section => section.short));
  const values = { lowest_short: names, section_name: names };
  const variant: SectionLeadVariant =
    levels.some(level => level === 'risk' || level === 'below_cutoff') ? 'prep'
    : balance === 'lopsided' ? 'uneven'
    : 'balanced';
  const copy =
    variant === 'prep' ? COPY.prep_cta
    : variant === 'uneven' ? COPY.fit_check.sections_uneven
    : COPY.fit_check.sections_balanced;

  return {
    body: interpolate(copy.body, values),
    headline: interpolate(copy.headline, values),
    label: copy.label,
    route: variant === 'prep' ? 'prep' : 'fit_check',
    variant,
  };
}

function lowestLead(lowest: readonly SectionAssessment[], spread: number): string {
  const { lowest_lead: copy } = COPY.section_feedback;
  const tied = lowest.length > 1;
  const template =
    spread === 0 ? copy.level
    : spread === 1 ?
      tied ? copy.tied_one
      : copy.one
    : tied ? copy.tied_other
    : copy.other;
  return interpolate(template, {
    lowest_short: joinNames(lowest.map(section => section.short)),
    percentile_ordinal: joinNames(lowest.map(section => section.percentileOrdinal)),
    spread,
    value: lowest[0]?.value ?? '',
  });
}

export type SectionFeedback = Readonly<{
  balance: BalanceLevel;
  balanceLabel: string;
  band: Band | null;
  carsCanada: CarsCanadaSummary | null;
  cutoffSchools: readonly CutoffSchool[];
  cutoffTitle: string;
  lead: SectionLead;
  lowest: readonly SectionAssessment[];
  lowestLead: string;
  retake: RetakeVerdict;
  retakeMessage: string;
  sections: readonly SectionAssessment[];
  spread: number;
  sumError: SectionSumError | null;
  total: number;
}>;

export function sectionFeedback(
  sections: SectionScores,
  expectedTotal?: number,
  schools: readonly School[] = SCHOOLS,
): SectionFeedback {
  const assessments = sectionAssessments(sections);
  const values = SECTION_KEYS.map(key => sections[key]);
  const total = values.reduce((sum, value) => sum + value, 0);
  const spread = Math.max(...values) - Math.min(...values);
  const balance = balanceForSpread(spread);
  const band = bandForScore(total);
  const levels = assessments.map(assessment => assessment.level);
  const lowest = lowestSections(assessments);
  const retake = retakeVerdict(band?.id ?? null, levels);

  const sumError =
    expectedTotal !== undefined && expectedTotal !== total ?
      {
        linkScore: isValidScore(total) ? total : null,
        message: interpolate(COPY.score_page.sections_sum_error, { score: expectedTotal, sum: total }),
        sum: total,
      }
    : null;

  return {
    balance,
    balanceLabel: COPY.section_feedback.balance[balance].label,
    band,
    carsCanada: carsCanadaSummary(sections.cars, schools),
    cutoffSchools: cutoffSchools(sections, schools),
    cutoffTitle: COPY.section_feedback.cutoff_list_title,
    lead: sectionLead(balance, levels, lowest),
    lowest,
    lowestLead: lowestLead(lowest, spread),
    retake,
    retakeMessage: COPY.section_feedback.retake[retake],
    sections: assessments,
    spread,
    sumError,
    total,
  };
}
